/**
 * HorizontalCardList Component
 * Reusable horizontal scrollable card container with responsive width
 * RTL-aware layout with proper scroll behavior - starts from RIGHT side
 * Uses CSS transform approach for RTL support
 */
import React, { ReactNode } from 'react';
import { ScrollView, View, Text, StyleSheet } from 'react-native';
import { useResponsive } from '../hooks/useResponsive';
import { FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

interface HorizontalCardListProps {
  children: ReactNode;
  emptyMessage?: string;
  showScrollIndicator?: boolean;
}

/**
 * HorizontalCardList - A container for horizontally scrollable cards
 * RTL Support: Uses transform scaleX(-1) to flip scroll direction
 * Cards start from the RIGHT side for Arabic layout
 *
 * IMPORTANT: Each child card should be wrapped with HorizontalCardWrapper
 * to flip it back to normal orientation.
 *
 * Usage:
 * ```tsx
 * const { horizontalCardWidth } = useResponsive();
 *
 * <HorizontalCardList emptyMessage="لا توجد عناصر">
 *   {items.map(item => (
 *     <View key={item.id} style={[styles.rtlCard, { width: horizontalCardWidth }]}>
 *       <MyCard item={item} />
 *     </View>
 *   ))}
 * </HorizontalCardList>
 * ```
 */
export default function HorizontalCardList({
  children,
  emptyMessage,
  showScrollIndicator = true,
}: HorizontalCardListProps) {
  const { colors } = useTheme();
  const { t } = useLanguage();

  const displayEmptyMessage = emptyMessage ?? t('common.noItems');

  // Check if children is empty
  const childArray = React.Children.toArray(children);
  const isEmpty = childArray.length === 0;

  if (isEmpty) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={[styles.emptyText, { color: colors.textMuted }]}>{displayEmptyMessage}</Text>
      </View>
    );
  }

  return (
    <View style={styles.rtlWrapper}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={showScrollIndicator}
        style={styles.scrollContainer}
        contentContainerStyle={styles.contentContainer}
      >
        {children}
      </ScrollView>
    </View>
  );
}

/**
 * HorizontalCard - Wrapper for individual cards in HorizontalCardList
 * Automatically applies responsive width and RTL transform fix
 */
interface HorizontalCardProps {
  children: ReactNode;
  style?: object;
}

export function HorizontalCard({ children, style }: HorizontalCardProps) {
  const { horizontalCardWidth } = useResponsive();
  const { colors } = useTheme();

  return (
    <View style={[styles.card, styles.rtlCard, { width: horizontalCardWidth, backgroundColor: colors.card, borderColor: colors.cardBorder }, style]}>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  // RTL wrapper - flips the entire ScrollView horizontally
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  scrollContainer: {
    flexGrow: 0,
  },
  contentContainer: {
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 4,
    paddingBottom: 12,
  },
  // RTL card - flips each card back to normal
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  emptyText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  card: {
    flexShrink: 0,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    overflow: 'hidden',
  },
});
